/**
 * Shared visual constants for the pour-wizard shell (Stepper chrome, dropdown
 * popovers, date/time picker theming) so PourCreate.tsx and PourSetup.tsx's
 * 4-step wizards look identical instead of drifting copies of each other.
 */

export const BRAND_GREEN = '#9AD02E';
export const BRAND_GREEN_DARK = '#88ba28';

// Shared popup style so Autocomplete (Project, Product) and Select (limit groups,
// weather) dropdowns render with a consistent paper, border and option sizing.
export const DROPDOWN_PAPER_SX = {
  mt: 0.5,
  border: '1px solid',
  borderColor: 'grey.300',
  borderRadius: '8px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  '& .MuiMenuItem-root, & .MuiAutocomplete-option': {
    fontSize: '0.875rem',
    minHeight: 36,
  },
} as const;

// Force every dropdown to open DOWNWARD, anchored to the bottom of the field, so
// Select menus stop positioning over the selected item and match the Autocompletes.
export const SELECT_MENU_PROPS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  slotProps: { paper: { sx: DROPDOWN_PAPER_SX } },
} as const;

export const DROPDOWN_AUTOCOMPLETE_SLOT_PROPS = {
  paper: { sx: DROPDOWN_PAPER_SX },
  popper: {
    placement: 'bottom-start' as const,
    // Don't let the listbox flip above the field — keep placement consistent.
    modifiers: [{ name: 'flip', enabled: false }],
  },
};

export const PICKER_POPPER_SX = {
  '& .MuiPaper-root': {
    border: '1px solid',
    borderColor: 'grey.400',
    borderRadius: '8px',
    // Strong drop shadow — the popup otherwise blends into the white page
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
  },
  '& .MuiPickersLayout-contentWrapper': {
    alignItems: 'stretch',
  },
  // Calendar: brand-green selected day + today ring
  '& .MuiPickersDay-root.Mui-selected': {
    backgroundColor: BRAND_GREEN,
    '&:hover, &:focus': { backgroundColor: BRAND_GREEN_DARK },
  },
  '& .MuiPickersDay-root.MuiPickersDay-today': {
    borderColor: BRAND_GREEN,
  },
  // Time: clearer, wider Hour / Min columns
  '& .MuiMultiSectionDigitalClock-root': {
    borderLeft: '1px solid',
    borderColor: 'grey.200',
  },
  '& .MuiMultiSectionDigitalClockSection-root': {
    width: 76,
    maxHeight: 292,
    scrollbarWidth: 'thin',
    '&:not(:first-of-type)': {
      borderLeft: '1px solid',
      borderColor: 'grey.100',
    },
    // Sticky column header so users know which list is which
    '&::before': {
      content: '"Hour"',
      position: 'sticky',
      top: 0,
      zIndex: 1,
      display: 'block',
      py: '6px',
      mb: '2px',
      textAlign: 'center',
      fontSize: '0.65rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      backgroundColor: '#fff',
      borderBottom: '1px solid',
      borderColor: 'grey.100',
    },
    '&:last-of-type::before': { content: '"Min"' },
  },
  '& .MuiMultiSectionDigitalClockSection-item': {
    minHeight: 34,
    width: 56,
    mx: 'auto',
    my: '2px',
    borderRadius: '6px',
    fontSize: '0.85rem',
    '&.Mui-selected': {
      backgroundColor: BRAND_GREEN,
      color: '#fff',
      fontWeight: 700,
      '&:hover, &:focus': { backgroundColor: BRAND_GREEN_DARK },
    },
  },
} as const;

// Desktop MUI Stepper theming (brand-green active/completed icons + connectors).
export const STEPPER_SX = {
  // Clear breathing room between the page header and the progress bar.
  mt: 5,
  mb: 4,
  // MUI centres each of the 4 step icons inside its quarter of the bar, which
  // leaves the bar ~25% narrower than the step content below it. Widen the
  // Stepper with negative side margins so the first and last 40px icons sit
  // flush with the content edges: with n steps and icon radius r the margin
  // m solves (W + 2m) / (2n) - m = r  =>  m = (W / (2n) - r) * 2n / (2n - 2).
  // For n = 4, r = 20 that is W / 6 - 26.67px.
  mx: 'calc(-100% / 6 + 26.67px)',
  '& .MuiStepLabel-label': {
    fontSize: '0.85rem',
    fontWeight: 500,
    mt: 1,
    whiteSpace: 'nowrap',
  },
  '& .MuiStepLabel-label.Mui-active': {
    fontWeight: 700,
  },
  '& .MuiStepIcon-root': {
    width: 40,
    height: 40,
  },
  '& .MuiStepIcon-root.Mui-active': {
    color: BRAND_GREEN,
  },
  '& .MuiStepIcon-root.Mui-completed': {
    color: BRAND_GREEN,
  },
  // MUI positions the alternativeLabel connector for its default 24px icons
  // (top 12px, 20px inset). With the 40px icons above, re-centre the line on
  // the circles (icon centre 20px minus half the 3px line) and inset it to
  // start/end at the circle edges plus the same 8px breathing room.
  '& .MuiStepConnector-root': {
    top: 18.5,
    left: 'calc(-50% + 28px)',
    right: 'calc(50% + 28px)',
  },
  '& .MuiStepConnector-line': {
    borderTopWidth: 3,
    borderRadius: 1.5,
  },
  '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
    borderColor: BRAND_GREEN,
  },
  '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
    borderColor: BRAND_GREEN,
  },
} as const;

// Primary wizard action (SAVE / NEXT). Colour, hover and floating shadow come
// from the app theme's containedPrimary override (App.tsx), so every primary
// button matches the login button; this just makes the wizard CTA a bit bolder.
export const PRIMARY_ACTION_SX = {
  fontWeight: 600,
} as const;

// Mobile compact progress-bar segment (reached = brand green, not yet reached = grey).
export const mobileProgressSegmentSx = (reached: boolean, visited: boolean) => ({
  flex: 1,
  height: 6,
  borderRadius: 3,
  backgroundColor: reached ? BRAND_GREEN : 'grey.300',
  cursor: visited ? 'pointer' : 'default',
  transition: 'background-color 0.2s',
});
